if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.content = this.querySelector('[role="document"]');
        this.scrollFrame = null;
        this.scrollEndTimer = null;
        this.closeButton = this.querySelector('[id^="ModalClose-"]');

        if (this.content) {
          this.content.addEventListener('click', this.onMediaClick.bind(this));
          this.content.addEventListener('keydown', this.onMediaKeydown.bind(this));
          this.content.addEventListener('scroll', this.onMediaScroll.bind(this), { passive: true });
        }
        this.closeButton?.addEventListener('touchend', this.onMobileClose.bind(this), { passive: false });
      }

      onMobileClose(event) {
        if (!this.mobileQuery.matches || !this.hasAttribute('open')) return;
        event.preventDefault();
        this.hide();
      }

      hide() {
        window.clearTimeout(this.scrollEndTimer);
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const mediaId = this.openedBy.getAttribute('data-media-id');
        const mediaItems = this.getMediaItems();
        const activeMedia = mediaItems.find((element) => element.dataset.mediaId === mediaId) || mediaItems[0];
        if (!activeMedia) return;

        mediaItems.forEach((element) => element.classList.toggle('active', element === activeMedia));

        if (this.mobileQuery.matches) {
          this.prepareMobileMedia(mediaItems);
          window.requestAnimationFrame(() => {
            this.content.scrollTo({ left: activeMedia.offsetLeft, behavior: 'auto' });
            this.updateMobileState(mediaItems.indexOf(activeMedia), mediaItems);
          });
          return;
        }

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      getMediaItems() {
        if (!this.content) return [];
        return Array.from(this.content.children).filter(
          (element) => element.hasAttribute('data-media-id') && window.getComputedStyle(element).display !== 'none'
        );
      }

      prepareMobileMedia(mediaItems) {
        mediaItems.forEach((element) => {
          if (element.nodeName !== 'IMG') return;
          element.setAttribute('role', 'button');
          element.setAttribute('tabindex', '0');
          element.setAttribute('aria-pressed', 'false');
          element.setAttribute('draggable', 'false');
        });

        if (!this.mobileCounter) {
          this.mobileCounter = document.createElement('div');
          this.mobileCounter.className = 'product-media-modal__counter';
          this.mobileCounter.setAttribute('role', 'status');
          this.mobileCounter.setAttribute('aria-live', 'polite');
          this.querySelector('.product-media-modal__dialog').appendChild(this.mobileCounter);
        }
      }

      onMediaClick(event) {
        if (!this.mobileQuery.matches || !this.hasAttribute('open')) return;
        const image = event.target.closest('img[data-media-id]');
        if (!image || !this.content.contains(image)) return;
        this.toggleZoom(image, event.clientX, event.clientY);
      }

      onMediaKeydown(event) {
        if (!this.mobileQuery.matches || !this.hasAttribute('open')) return;
        const image = event.target.closest('img[data-media-id]');
        if (!image) return;

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          const bounds = image.getBoundingClientRect();
          this.toggleZoom(image, bounds.left + bounds.width / 2, bounds.top + bounds.height / 2);
        }

        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.resetZoom();
          this.content.scrollBy({
            left: event.key === 'ArrowRight' ? this.content.clientWidth : -this.content.clientWidth,
            behavior: 'smooth',
          });
        }
      }

      toggleZoom(image, clientX, clientY) {
        const shouldZoom = !image.classList.contains('is-zoomed');
        this.resetZoom();

        if (shouldZoom) {
          const bounds = image.getBoundingClientRect();
          const originX = Math.min(80, Math.max(20, ((clientX - bounds.left) / bounds.width) * 100));
          const originY = Math.min(80, Math.max(20, ((clientY - bounds.top) / bounds.height) * 100));
          image.style.setProperty('--product-gallery-zoom-x', `${originX}%`);
          image.style.setProperty('--product-gallery-zoom-y', `${originY}%`);
          image.classList.add('is-zoomed');
          image.setAttribute('aria-pressed', 'true');
        }
      }

      resetZoom(except) {
        this.querySelectorAll('.product-media-modal__content > img.is-zoomed').forEach((image) => {
          if (image === except) return;
          image.classList.remove('is-zoomed');
          image.setAttribute('aria-pressed', 'false');
          image.style.removeProperty('--product-gallery-zoom-x');
          image.style.removeProperty('--product-gallery-zoom-y');
        });
      }

      onMediaScroll() {
        if (!this.mobileQuery.matches || !this.hasAttribute('open') || this.scrollFrame) return;
        window.clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = window.setTimeout(() => this.snapToClosestMedia(), 160);
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          const mediaItems = this.getMediaItems();
          if (!mediaItems.length) return;
          const index = Math.min(
            mediaItems.length - 1,
            Math.max(0, Math.round(this.content.scrollLeft / this.content.clientWidth))
          );
          this.updateMobileState(index, mediaItems);
        });
      }

      snapToClosestMedia() {
        if (!this.mobileQuery.matches || !this.hasAttribute('open') || !this.content.clientWidth) return;
        const mediaItems = this.getMediaItems();
        const index = Math.min(
          mediaItems.length - 1,
          Math.max(0, Math.round(this.content.scrollLeft / this.content.clientWidth))
        );
        const target = index * this.content.clientWidth;
        if (Math.abs(this.content.scrollLeft - target) > 1) {
          this.content.scrollTo({ left: target, behavior: 'smooth' });
        }
        this.updateMobileState(index, mediaItems);
      }

      updateMobileState(index, mediaItems = this.getMediaItems()) {
        mediaItems.forEach((element, mediaIndex) => {
          const active = mediaIndex === index;
          element.classList.toggle('active', active);
          if (active) element.setAttribute('aria-current', 'true');
          else element.removeAttribute('aria-current');
        });
        this.resetZoom(mediaItems[index]);
        if (this.mobileCounter) this.mobileCounter.textContent = `${index + 1} / ${mediaItems.length}`;
      }
    }
  );
}
